import { brand, theme } from '@/lib/platform'

interface BrandLogoProps {
  /** Use the negative (light-on-dark) variant for dark surfaces. */
  variant?: 'primary' | 'negative'
  /** Force a text-only render even if a logo asset is configured. */
  textOnly?: boolean
  /** Image height in px (only used when rendering as <img>). */
  height?: number
  /** Tailwind size class for the text variant. */
  textClassName?: string
  className?: string
}

/**
 * Renders the platform brand mark.
 *
 * Default behavior: shows `brand.name` as styled text — neutral and template-safe.
 * If you want a custom image logo per client, drop an asset in `/public/`,
 * point `theme.logos.primary` (or `.negative`) at it, and pass a non-default
 * value. The text fallback fires whenever the configured logo path is empty
 * or `textOnly` is set.
 */
export function BrandLogo({
  variant = 'primary',
  textOnly,
  height = 18,
  textClassName = 'text-lg font-semibold tracking-tight',
  className = '',
}: BrandLogoProps) {
  const logoPath = variant === 'negative' ? theme.logos.negative : theme.logos.primary
  const hasCustomLogo = !textOnly && logoPath && logoPath !== '/logo.svg' && logoPath !== '/logo_negative.svg'

  if (hasCustomLogo) {
    return (
      <img
        src={logoPath}
        alt={brand.name}
        height={height}
        className={className}
        loading="eager"
      />
    )
  }

  return (
    <span className={`${textClassName} ${className}`} aria-label={brand.name}>
      {brand.name}
    </span>
  )
}
